type OscType = 'sine' | 'square' | 'sawtooth' | 'triangle';

interface WebkitWindow {
  webkitAudioContext?: typeof AudioContext;
}

let ctx: AudioContext | null = null;
let soundOn = true;
let musicOn = false;
let musicIntervalId: number | null = null;

function getCtx(): AudioContext | null {
  if (!ctx) {
    try {
      const Ctor =
        window.AudioContext ?? (window as unknown as WebkitWindow).webkitAudioContext;
      if (!Ctor) return null;
      ctx = new Ctor();
    } catch {
      return null;
    }
  }
  if (ctx.state === 'suspended') {
    void ctx.resume();
  }
  return ctx;
}

function tone(freq: number, dur: number, type: OscType, gain: number, when = 0): void {
  if (!soundOn) return;
  const ac = getCtx();
  if (!ac) return;
  const t0 = ac.currentTime + when;
  const osc = ac.createOscillator();
  const g = ac.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(gain, t0 + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(g).connect(ac.destination);
  osc.start(t0);
  osc.stop(t0 + dur + 0.05);
}

function slide(f1: number, f2: number, dur: number, type: OscType, gain: number): void {
  if (!soundOn) return;
  const ac = getCtx();
  if (!ac) return;
  const t0 = ac.currentTime;
  const osc = ac.createOscillator();
  const g = ac.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(f1, t0);
  osc.frequency.exponentialRampToValueAtTime(f2, t0 + dur);
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(gain, t0 + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(g).connect(ac.destination);
  osc.start(t0);
  osc.stop(t0 + dur + 0.05);
}

const MUSIC_NOTES = [262, 330, 392, 523, 392, 330];
const MUSIC_STEP_MS = 280;

function startMusic(): void {
  if (!musicOn) return;
  if (musicIntervalId !== null) return;
  let step = 0;
  musicIntervalId = window.setInterval(() => {
    if (!musicOn) return;
    const f = MUSIC_NOTES[step % MUSIC_NOTES.length];
    if (f !== undefined) tone(f, 0.18, 'triangle', 0.05);
    step += 1;
  }, MUSIC_STEP_MS);
}

function stopMusic(): void {
  if (musicIntervalId !== null) {
    clearInterval(musicIntervalId);
    musicIntervalId = null;
  }
}

export const Audio = {
  setSound(on: boolean): void {
    soundOn = on;
  },
  setMusic(on: boolean): void {
    musicOn = on;
    if (on) startMusic();
    else stopMusic();
  },
  pop(): void {
    slide(800, 1500, 0.12, 'sine', 0.22);
  },
  click(): void {
    slide(600, 900, 0.08, 'triangle', 0.18);
  },
  correct(): void {
    tone(660, 0.1, 'sine', 0.18);
    window.setTimeout(() => tone(880, 0.15, 'sine', 0.18), 60);
  },
  wrong(): void {
    slide(300, 150, 0.25, 'square', 0.14);
  },
  cheer(): void {
    [523, 659, 784, 1047].forEach((f, i) => {
      window.setTimeout(() => tone(f, 0.18, 'triangle', 0.16), i * 80);
    });
  },
  sad(): void {
    [523, 466, 392].forEach((f, i) => {
      window.setTimeout(() => tone(f, 0.3, 'sine', 0.18), i * 150);
    });
  },
  tick(): void {
    tone(1200, 0.04, 'square', 0.08);
  },
  combo(level: number): void {
    const base = 600 + level * 60;
    tone(base, 0.08, 'triangle', 0.18);
    window.setTimeout(() => tone(base * 1.5, 0.12, 'triangle', 0.16), 50);
  },
};
